import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, Inject, NgModule, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { RouterModule } from '@angular/router';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { Constants } from '@app/common/constants';
import { Roles } from '@app/common/roles';
import { ErrorTemplatesModule } from '@app/components/error-templates/error-templates.component';
import { PopoverModule } from '@app/components/popover/popover.component';
import { TableOfContentsModule } from '@app/components/table-of-contents/table-of-contents.component';
import { TextPlaceholderModule } from '@app/components/text-placeholder/text-placeholder.component';
import { WidgetPlaceholderModule } from '@app/components/widget-placeholder/widget-placeholder.component';
import { StickyFillModule } from '@app/directives/stickyfill.directive';
import { SearchResult } from '@app/models/search-result';
import { SiteGroupTeam } from '@app/models/site-group-team';
import { LinkModule } from '@app/directives/link.directive';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { GroupsService } from '@app/services/groups.service';
import { SearchService } from '@app/services/search.service';
import { SecurityService } from '@app/services/security.service';
import { UrlHelperService } from '@app/services/url-helper.service';
import { Environment } from '@environments/interface.environment';
import { Group } from '@microsoft/microsoft-graph-types-beta';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, finalize, map, startWith } from 'rxjs/operators';
import { AutoFocusModule } from '../../directives/auto-focus.directive';

export enum ViewMode {
  YourSites = 0,
  AllSites = 1
}

@Component({
  selector: 'app-site-index',
  templateUrl: './site-index.component.html',
  styleUrls: ['./site-index.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class SiteIndexComponent implements OnInit {
  siteIndexForm = new FormGroup({
    filter: new FormControl()
  });
  filter$: Observable<string>;
  selectedMode = ViewMode.YourSites;
  mySitesGroupedByLetters$: Observable<Map<string, SiteGroupTeam[]>>;
  allSitesGroupedByLetters$: Observable<Map<string, SiteGroupTeam[]>>;
  allowedToCreateNew$: Observable<boolean>;
  errorSubject$ = new Subject();
  enablePopover = this.featureSettingsStoreService.state.enableSiteIndexPopover;
  componentName = 'Your collaboration sites';
  constructor(
    @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
    private searchService: SearchService,
    private groupsService: GroupsService,
    private urlHelperService: UrlHelperService,
    private securityService: SecurityService,
    public featureSettingsStoreService: FeatureSettingsStoreService) { }

  ngOnInit() {

    if (this.featureSettingsStoreService.state.everyoneCanCreateCollabSites) {
      this.allowedToCreateNew$ = of(true);
    } else {
      this.allowedToCreateNew$ = this.securityService.hasUserAppRole(Roles.CollabSiteCreator);
    }

    this.filter$ = this.siteIndexForm
      .get('filter')
      .valueChanges
      .pipe(
        startWith(''),
        debounceTime(300),
        finalize(() => {
          // Reset filter when we switch between your sites and all sites
          this.siteIndexForm.get('filter').patchValue('');
        })
      );

    this.mySitesGroupedByLetters$ = combineLatest([
      this.filter$,
      this.searchService.getAllSiteCollections(),
      this.groupsService.getAllMyOwnedGroups(true),
      this.groupsService.getAllMyMemberGroups(true)])
      .pipe(
        map(([filterString, siteSearchResults, ownedGroups, memberGroups]) => {
          const mapOfSites = new Map<string, SiteGroupTeam[]>(); // The real map to return

          const uniqueMapOfOwnerAndMemberGroups = new Map<string, Group>();
          ownedGroups.concat(memberGroups).forEach((group) => {
            // Concat the owner and membergroups and then combine them into one map to get rid of duplicates.
            uniqueMapOfOwnerAndMemberGroups.set(group.id, Object.assign(uniqueMapOfOwnerAndMemberGroups.get(group.id) || {}, group));
          });
          const ownerAndMemberGroups = Array.from(uniqueMapOfOwnerAndMemberGroups.values());

          ownerAndMemberGroups
            .filter((group) => this.doesGroupNameContainString(filterString, group))
            .sort((a, b) => a.displayName.localeCompare(b.displayName))
            .forEach(group => {
              const siteGroupTeam = this.buildSiteAndGroupTeam(siteSearchResults, group);
              this.assignSiteToMap(siteGroupTeam, mapOfSites);
            });
          return mapOfSites;
        }),
        catchError(err => {
          this.errorSubject$.next(err);
          return of(null);
        }));

    this.allSitesGroupedByLetters$ = combineLatest([
      this.filter$,
      this.searchService.getAllSiteCollections(),
      this.groupsService.getAllGroups(true)])
      .pipe(
        map(([filterString, siteSearchResults, groups]) => {
          const mapOfSites = new Map<string, SiteGroupTeam[]>();
          groups
            .filter(group => this.doesGroupNameContainString(filterString, group))
            .sort((a, b) => a.displayName.localeCompare(b.displayName))
            .forEach(group => {
              const siteGroupTeam = this.buildSiteAndGroupTeam(siteSearchResults, group);
              this.assignSiteToMap(siteGroupTeam, mapOfSites);
            });
          return mapOfSites;
        }),
        catchError(err => {
          this.errorSubject$.next(err);
          return of(null);
        }));
  }

  private doesGroupNameContainString(filterString: string, group: Group) {
    if (filterString) {
      return group.displayName.toLowerCase().indexOf(filterString.toLowerCase()) > -1;
    }
    return true;
  }

  private buildSiteAndGroupTeam(siteSearchResults: SearchResult[], group: Group) {
    const site = siteSearchResults.filter(s => s.GroupId).find(s => s.GroupId.toLowerCase() === group.id.toLowerCase());
    const hasTeam = group.resourceProvisioningOptions.indexOf('Team') > -1;
    let url: string;
    if (site) {
      if (hasTeam) {
        url = `${site.Path}/_layouts/groupstatus.aspx?id=${group.id}&Target=TEAM`;
      } else {
        url = site.Path;
      }
    } else {
      url = `${this.urlHelperService.getAuthorityUrlWithoutQueryStrings(this.environment.coreWorkplaceSiteUrl)}/_layouts/groupstatus.aspx?id=${group.id}&target=site`;
    }
    const sitePath = `${site ? site.Path : this.urlHelperService.getAuthorityUrlWithoutQueryStrings(this.environment.coreWorkplaceSiteUrl)}`;
    const baseUrl = `${sitePath}/_layouts/groupstatus.aspx?id=${group.id}&target=`;
    const siteGroupTeam = {
      Url: url,
      HasTeam: hasTeam,
      HasYammer: group['creationOptions'] ? group['creationOptions'].indexOf('YammerProvisioning') > -1 : false,
      SiteUrl: site ? site.Path : url,
      Title: group.displayName,
      Description: group.description,
      Visibility: group.visibility,
      GroupId: group.id,
      ConversationUrl: `${baseUrl}${Constants.COLLAB_UNIFIED_GROUPS_CONVERSATION_LINK_TARGET}`,
      FilesUrl: `${baseUrl}${Constants.COLLAB_UNIFIED_GROUPS_FILES_LINK_TARGET}`,
      CalendarUrl: `${baseUrl}${Constants.COLLAB_UNIFIED_GROUPS_CALENDER_LINK_TARGET}`,
      PlannerUrl: `${baseUrl}${Constants.COLLAB_UNIFIED_GROUPS_PLANNER_LINK_TARGET}`,
    } as SiteGroupTeam;
    return siteGroupTeam;
  }

  private assignSiteToMap(siteGroupTeam: SiteGroupTeam, mapOfSites: Map<string, SiteGroupTeam[]>) {
    const firstChar = siteGroupTeam.Title.charAt(0).toUpperCase();
    if (/[ !@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/i.test(firstChar) || /^\d*$/i.test(firstChar)) {
      if (mapOfSites.has('#')) {
        mapOfSites.get('#').push(siteGroupTeam);
      } else {
        mapOfSites.set('#', [siteGroupTeam]);
      }
    } else {
      if (mapOfSites.has(firstChar)) {
        mapOfSites.get(firstChar).push(siteGroupTeam);
      } else {
        mapOfSites.set(firstChar, [siteGroupTeam]);
      }
    }
  }
}

@NgModule({
  declarations: [SiteIndexComponent],
  exports: [SiteIndexComponent],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    LinkModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    TableOfContentsModule,
    ErrorTemplatesModule,
    MatRadioModule,
    WidgetPlaceholderModule,
    OverlayModule,
    PopoverModule,
    TextPlaceholderModule,
    StickyFillModule,
    AutoFocusModule
  ]
})
export class SiteIndexModule { }
